import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGlobalPlayer } from '@/context/GlobalPlayerContext';
import { Play, Pause, SkipBack, SkipForward, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatStorageUrl } from '@/lib/supabase';

const MiniPlayer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const { 
    currentSong, 
    isPlaying,
    showMiniPlayer,
    togglePlayPause,
    playPreviousSong,
    playNextSong,
    stop,
    canPerformAction,
    playerState,
    emergencyReset
  } = useGlobalPlayer();

  // Don't render if no song, shouldn't show, or on player page
  if (!currentSong || !showMiniPlayer || location.pathname === '/player') {
    return null;
  }

  const handleNavigateToPlayer = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate('/player');
  };

  const handleStop = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (canPerformAction('stop')) {
      stop();
    } else {
      emergencyReset();
    }
  };

  return (
    <div 
      className="fixed bottom-20 left-0 right-0 bg-background/90 backdrop-blur-sm border-t cursor-pointer z-40 border border-[#FFD700] border-solid rounded-xl h-12"
      style={{
        marginBottom: 'max(env(safe-area-inset-bottom), 8px)'
      }}
      onClick={handleNavigateToPlayer}
    >
      <div className="container mx-auto px-2 h-full flex items-center">
        <div className="flex items-center w-full">
          {/* Capa e título */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="relative w-7 h-7 flex-shrink-0">
              {!imageLoaded && (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 rounded animate-pulse" />
              )}
              <img 
                src={formatStorageUrl(currentSong.cover_url || currentSong.capa_url)}
                alt={`${currentSong.livro} ${currentSong.capitulo}`}
                className={cn(
                  "w-7 h-7 rounded object-cover",
                  imageLoaded ? "opacity-100" : "opacity-0",
                  "transition-opacity duration-200"
                )}
                loading="eager"
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div className="hidden w-7 h-7 rounded bg-gradient-to-br from-blue-400 to-purple-500 items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            </div>
            <p className="font-medium text-xs text-foreground whitespace-normal break-words max-w-full leading-tight">
              {currentSong.descricao}
            </p>
          </div>
          {/* Controles minimalistas alinhados à direita */}
          <div className="flex items-center gap-1 ml-auto pr-1">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={e => { 
                e.stopPropagation(); 
                e.preventDefault();
                playPreviousSong(); 
              }}
              className="rounded-full p-1 h-8 w-8 min-w-[44px] min-h-[44px] touch-manipulation"
            >
              <SkipBack className="h-3.5 w-3.5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={e => { 
                e.stopPropagation(); 
                e.preventDefault();
                if (canPerformAction('play-song') || canPerformAction('pause')) {
                  togglePlayPause(); 
                }
              }}
              className="rounded-full p-1 h-9 w-9 min-w-[44px] min-h-[44px] touch-manipulation"
            >
              {isPlaying ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={e => { 
                e.stopPropagation(); 
                e.preventDefault();
                playNextSong(); 
              }}
              className="rounded-full p-1 h-8 w-8 min-w-[44px] min-h-[44px] touch-manipulation"
            >
              <SkipForward className="h-3.5 w-3.5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleStop}
              className="rounded-full p-1 h-8 w-8 bg-red-100 hover:bg-red-300 transition-colors ml-2 min-w-[44px] min-h-[44px] touch-manipulation"
            >
              <X className="h-3.5 w-3.5 text-red-600" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniPlayer; 